import type { UsageDay } from "../api/types";
import { daysInMonth } from "./months";

export interface CalendarCell {
  day: string;
  dayOfMonth: number;
  /** 0 = Monday … 6 = Sunday, so the grid starts the week the way a roster does. */
  weekday: number;
  peakPeople: number | null;
  peakDevices: number | null;
  included: number | null;
  over: boolean;
  peakNames: string[];
}

const TREE = "*";

/** One cell per day of the month from the tree rows (`team === "*"`). A row
 *  remembers the allowance it was measured against; a row without one uses
 *  the tree's current allowance. */
export function calendarCells(
  month: string,
  days: UsageDay[],
  included: number | null,
): CalendarCell[] {
  const tree = new Map(days.filter((d) => d.team === TREE).map((d) => [d.day, d]));
  const cells: CalendarCell[] = [];
  for (let d = 1; d <= daysInMonth(month); d += 1) {
    const day = `${month}-${String(d).padStart(2, "0")}`;
    const row = tree.get(day);
    const allowance = row?.included ?? included;
    const peak = row?.peakPeople ?? null;
    cells.push({
      day,
      dayOfMonth: d,
      weekday: (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7,
      peakPeople: peak,
      peakDevices: row?.peakDevices ?? null,
      included: allowance,
      over: peak !== null && allowance !== null && peak > allowance,
      peakNames: row?.peakNames ?? [],
    });
  }
  return cells;
}

export interface TeamDailyRow {
  day: string;
  tree: number | null;
  byTeam: Record<string, number | null>;
  over: boolean;
}

/** One row per day that has any data, in day order; `teams` fixes the column order. */
export function teamDailyRows(
  days: UsageDay[],
  teams: string[],
  included: number | null,
): TeamDailyRow[] {
  const byDay = new Map<string, UsageDay[]>();
  for (const d of days) byDay.set(d.day, [...(byDay.get(d.day) ?? []), d]);
  return [...byDay.keys()].sort().map((day) => {
    const rows = byDay.get(day) ?? [];
    const tree = rows.find((r) => r.team === TREE) ?? null;
    const allowance = tree?.included ?? included;
    const byTeam: Record<string, number | null> = {};
    for (const t of teams) byTeam[t] = rows.find((r) => r.team === t)?.peakPeople ?? null;
    return {
      day,
      tree: tree?.peakPeople ?? null,
      byTeam,
      over: tree !== null && allowance !== null && tree.peakPeople > allowance,
    };
  });
}
